import { useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { HoverHelp } from '@/tableControls'
import { importWarehouseLedgerBsLogs } from '@/services/warehouse'
import { isBrightSitesAuthError } from '@/services/brightsitesExtension'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'

type WarehouseBrightSitesLedgerSyncProps = {
  busy?: boolean
  authRecoveryHint?: boolean
  onAuthError?: () => void
  onSynced?: () => void | Promise<void>
}

export default function WarehouseBrightSitesLedgerSync({
  busy = false,
  authRecoveryHint = false,
  onAuthError,
  onSynced,
}: WarehouseBrightSitesLedgerSyncProps) {
  const [syncing, setSyncing] = useState(false)

  async function handleSync() {
    if (busy || syncing) return
    setSyncing(true)
    try {
      const result = await importWarehouseLedgerBsLogs()
      const parts = [
        result.created > 0 ? `${result.created} created` : null,
        result.updated > 0 ? `${result.updated} updated` : null,
        result.skipped > 0 ? `${result.skipped} skipped` : null,
      ].filter(Boolean)
      toast.success(
        parts.length > 0
          ? `Bright Sites sync: ${parts.join(', ')}.`
          : result.message || 'Bright Sites sync complete.'
      )
      await onSynced?.()
    } catch (err) {
      if (isBrightSitesAuthError(err)) onAuthError?.()
      toast.error(formatApiError(err))
    } finally {
      setSyncing(false)
    }
  }

  const disabled = busy || syncing
  const tooltip = (
    <div className="space-y-1.5">
      <p>
        Import or re-sync ledger transactions from Bright Sites. Overridden fields are preserved;
        rows with upstream drift are marked Upstream changed.
      </p>
      {authRecoveryHint ? (
        <p className="text-amber-800">
          Bright Sites session expired — reconnect in Warehouse &gt; Settings.
        </p>
      ) : null}
    </div>
  )

  return (
    <HoverHelp aria-label="Sync from Bright Sites help" label={
      <button
        type="button"
        className="btn btn-secondary inline-flex items-center gap-1.5 text-sm"
        disabled={disabled}
        onClick={() => void handleSync()}
      >
        <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} aria-hidden />
        {syncing ? 'Syncing…' : 'Sync from Bright Sites'}
      </button>
    }>
      {tooltip}
    </HoverHelp>
  )
}
